/**
 * PrioritySelect Component
 *
 * Dropdown for selecting task priority.
 */

import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Flag } from 'lucide-react';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { Priority } from '@/types';

const priorityOptions: { value: Priority | ''; label: string; color: string }[] = [
  { value: '', label: 'Nessuna', color: 'text-text-quaternary' },
  { value: 'low', label: 'Bassa', color: 'text-accent-cyan' },
  { value: 'normal', label: 'Normale', color: 'text-accent-amber' },
  { value: 'high', label: 'Alta', color: 'text-accent-orange' },
  { value: 'urgent', label: 'Urgente', color: 'text-accent-rose' },
];

export interface PrioritySelectProps {
  value: Priority | null;
  onChange: (priority: Priority | null) => void;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  disabled?: boolean;
}

export function PrioritySelect({
  value,
  onChange,
  size = 'md',
  showLabel = true,
  disabled = false,
}: PrioritySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 6,
    minWidth: 140,
    matchTriggerWidth: showLabel,
  });

  const currentPriority =
    priorityOptions.find((p) => p.value === (value || '')) || priorityOptions[0];

  useClickOutside([dropdownRef, triggerRef], () => setIsOpen(false), isOpen);

  const handleSelect = useCallback(
    (priority: Priority | '') => {
      onChange(priority || null);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div ref={triggerRef} className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-1 rounded-md transition-all whitespace-nowrap bg-surface-tertiary',
          size === 'sm' ? 'px-1.5 py-1 h-6' : 'px-2 py-1.5 h-7',
          !disabled && 'hover:bg-surface-hover cursor-pointer',
          disabled && 'cursor-default opacity-70'
        )}
        title={disabled ? undefined : 'Cambia priorità'}
      >
        <Flag
          className={clsx(
            size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5',
            'shrink-0',
            currentPriority.color
          )}
        />
        {showLabel && (
          <>
            <span
              className={clsx(
                'font-medium capitalize truncate',
                size === 'sm' ? 'text-[10px]' : 'text-[11px]',
                currentPriority.color
              )}
            >
              {currentPriority.label}
            </span>
            {!disabled && (
              <ChevronDown
                className={clsx(
                  size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3',
                  'shrink-0 text-text-quaternary'
                )}
              />
            )}
          </>
        )}
      </button>

      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="py-1 rounded-lg shadow-2xl min-w-[120px] animate-fade-in border border-border-primary/70 bg-surface-primary"
          >
            {priorityOptions.map((option) => (
              <button
                key={option.value || 'none'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option.value as Priority | '');
                }}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-white/10 transition-colors',
                  option.value === value && 'bg-white/10'
                )}
              >
                <Flag className={clsx('w-3 h-3', option.color)} />
                <span className="text-text-primary">{option.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
